import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { ApiService } from './api.service';
import { SavedPhoto } from './photo-upload.service';

// Component-scoped: one gallery and cursor per open job/project screen.
@Injectable()
export class PhotoGalleryService implements OnDestroy {
  private readonly api = inject(ApiService);
  readonly photos = signal<SavedPhoto[]>([]);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly hasMore = signal(false);
  readonly total = signal(0);
  readonly error = signal('');
  private reference = '';
  private model: 'Job' | 'Project' = 'Job';
  private cursor: number | null = null;
  private request?: Subscription;

  reset(reference: string, model: 'Job' | 'Project') {
    this.request?.unsubscribe();
    this.reference = reference;
    this.model = model;
    this.photos.set([]);
    this.total.set(0);
    this.cursor = null;
    this.hasMore.set(false);
    this.loading.set(false);
    this.loadingMore.set(false);
    this.load();
  }

  add(photo: SavedPhoto) {
    const existing = this.photos().findIndex(row => String(row.id) === String(photo.id));
    if (existing >= 0) this.photos.update(rows => rows.map((row, index) => index === existing ? photo : row));
    else {
      this.photos.update(rows => [...rows, photo]);
      this.total.update(total => Math.max(total + 1, this.photos().length));
    }
  }

  loadMore() { if (this.hasMore()) this.load(); }
  retry() { this.load(); }

  private load() {
    if (!this.reference || this.loading() || this.loadingMore()) return;
    const first = this.cursor === null;
    (first ? this.loading : this.loadingMore).set(true);
    this.error.set('');
    this.request = this.api.getPhotoPage(this.reference, this.model, this.cursor).subscribe({
      next: result => {
        if (!result.success) { this.fail(result.message || 'Photos could not be loaded.'); return; }
        const incoming = result.data?.photos || [];
        const ids = new Set(this.photos().map(photo => String(photo.id)));
        this.photos.update(current => [...current, ...incoming.filter(photo => !ids.has(String(photo.id)))]);
        this.total.set(Math.max(result.data?.pagination?.total ?? this.photos().length, this.photos().length));
        this.hasMore.set(!!result.data?.pagination?.has_more);
        this.cursor = result.data?.pagination?.next_before_id ?? null;
        this.loading.set(false);
        this.loadingMore.set(false);
      },
      error: () => this.fail('Photos could not be loaded. Check your connection and retry.')
    });
  }

  private fail(message: string) {
    this.error.set(message);
    this.loading.set(false);
    this.loadingMore.set(false);
  }
  ngOnDestroy() { this.request?.unsubscribe(); }
}
